import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Fragment, useState } from "react";
import { ArrowUpRight, BanknoteIcon, Download, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AgeVerificationStatus } from "@/components/studio/age-verification-status";
import { ConnectChecklist } from "@/components/studio/connect-checklist";
import { EmptyState } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadPayoutCsv, downloadPayoutPdf } from "@/lib/payments/payout-exports";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getPayoutOverview,
  openPayoutDashboard,
  refreshPayoutAccount,
  requestPayout,
  startPayoutOnboarding,
  cancelPayoutRequest,
  getPayoutRequestTimeline,
} from "@/lib/payments/connect.functions";

/**
 * Stripe refuses to create connected accounts until the platform profile
 * questionnaire is answered. That message is meaningless to a writer, so it is
 * translated into something they can act on (and it is our job, not theirs).
 */
function friendlyOnboardingError(message?: string | null): string {
  const text = message ?? "Could not open payout setup";
  if (/platform profile|connect\/accounts\/overview/i.test(text)) {
    return "Payout setup isn't open yet — Quibblies is finishing its payment provider verification. Please try again shortly; nothing is lost and your balance keeps building.";
  }
  return text;
}

const TIMELINE_LABEL: Record<string, string> = {
  requested: "Request received",
  approved: "Approved by the team",
  processing: "Payment being sent",
  paid: "Payment sent",
  failed: "Payment failed",
  rejected: "Request declined",
  cancelled: "Withdrawn by you",
};

/** The step-by-step history of one payout request, with any error detail. */
function RequestTimeline({ requestId }: { requestId: string }) {
  const load = useServerFn(getPayoutRequestTimeline);
  const timeline = useQuery({
    queryKey: ["payout-request-timeline", requestId],
    queryFn: () => load({ data: { requestId } }),
  });

  if (timeline.isLoading) return <Skeleton className="h-16 w-full rounded-lg" />;
  if (timeline.error)
    return <p className="text-xs text-destructive">{timeline.error.message}</p>;
  if ((timeline.data ?? []).length === 0)
    return <p className="text-xs text-muted-foreground">No updates recorded yet.</p>;

  return (
    <ol className="space-y-2 border-l border-border/70 pl-4">
      {timeline.data!.map((event) => (
        <li key={event.id} className="text-xs">
          <span className="font-medium text-foreground">
            {TIMELINE_LABEL[event.status] ?? event.status}
          </span>{" "}
          <time className="text-muted-foreground" dateTime={event.created_at}>
            {new Date(event.created_at).toLocaleString()}
          </time>
          {event.detail ? (
            <p className="text-muted-foreground">{event.detail}</p>
          ) : null}
          {event.error_detail ? (
            <p className="text-destructive">{event.error_detail}</p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export const Route = createFileRoute("/_authenticated/studio/payouts")({
  head: () => ({
    meta: [
      { title: "Payouts & earnings — Creator Studio — Quibblies" },
      {
        name: "description",
        content:
          "Track tips you've received, your Quibblies payout account, balances awaiting settlement and every payout already sent to your bank.",
      },
      { property: "og:title", content: "Payouts & earnings — Quibblies Creator Studio" },
      {
        property: "og:description",
        content: "Balances, payout requests and settlement history for Quibblies writers.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PayoutsPage,
});

function money(minor: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "GBP",
  }).format(minor / 100);
}

function shortDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const REQUEST_TONE: Record<string, string> = {
  requested: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  approved: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
  paid: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  rejected: "bg-destructive/15 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
};

function PayoutsPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(getPayoutOverview);
  const onboard = useServerFn(startPayoutOnboarding);
  const refresh = useServerFn(refreshPayoutAccount);
  const dashboard = useServerFn(openPayoutDashboard);
  const submitRequest = useServerFn(requestPayout);
  const cancelRequest = useServerFn(cancelPayoutRequest);

  const [notes, setNotes] = useState<Record<string, string>>({});
  const [onboardingError, setOnboardingError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["writer-payouts"],
    queryFn: () => load({ data: {} }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["writer-payouts"] });

  const onboardMutation = useMutation({
    mutationFn: () =>
      onboard({ data: { returnUrl: `${window.location.origin}/studio/payouts` } }),
    onSuccess: (result) => {
      if (result.url) {
        setOnboardingError(null);
        window.location.href = result.url;
      } else {
        setOnboardingError(result.error ?? null);
        toast.error(friendlyOnboardingError(result.error));
      }
    },
    onError: (error: Error) => {
      setOnboardingError(error.message);
      toast.error(friendlyOnboardingError(error.message));
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => refresh({ data: {} }),
    onSuccess: (result) => {
      if (result.ok) {
        toast.success("Payout account refreshed");
        void invalidate();
      } else toast.error(result.error ?? "Could not refresh");
    },
  });

  const dashboardMutation = useMutation({
    mutationFn: () => dashboard({ data: {} }),
    onSuccess: (result) => {
      if (result.url) window.open(result.url, "_blank", "noopener");
      else toast.error(result.error ?? "Could not open your payout dashboard");
    },
  });

  const requestMutation = useMutation({
    mutationFn: (currency: string) =>
      submitRequest({ data: { currency, note: notes[currency] ?? "" } }),
    onSuccess: (result, currency) => {
      if (result.ok) {
        toast.success("Payout requested — our team will review it shortly.");
        setNotes((prev) => ({ ...prev, [currency]: "" }));
        void invalidate();
      } else toast.error(result.error ?? "Could not request a payout");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelRequest({ data: { id } }),
    onSuccess: () => {
      toast.success("Request withdrawn");
      void invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        title="We couldn't load your earnings"
        description="Something went wrong reading your payout details. Please refresh the page and try again."
      />
    );
  }

  const connect = data.connect;
  const connected = Boolean(connect?.chargesEnabled && connect?.payoutsEnabled);

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="font-display text-3xl">Payouts &amp; earnings</h1>
        <p className="max-w-2xl text-muted-foreground">
          Readers tip you directly. Quibblies keeps a small platform fee and the rest is yours.
          Connect a payout account and Stripe sends your share to your bank automatically — no
          waiting for anyone to approve it.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => downloadPayoutCsv(data)}>
            <Download className="mr-1 h-4 w-4" aria-hidden /> Download CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void downloadPayoutPdf(data).catch(() =>
                toast.error("Could not build your PDF statement"),
              );
            }}
          >
            <Download className="mr-1 h-4 w-4" aria-hidden /> Download PDF statement
          </Button>
        </div>
      </header>

      <AgeVerificationStatus state={data.ageVerification} />

      {!connected ? (
        <ConnectChecklist
          account={connect}
          ageVerified={data.ageVerified}
          errorMessage={onboardingError}
          onStart={() => onboardMutation.mutate()}
          onRefresh={() => refreshMutation.mutate()}
          starting={onboardMutation.isPending}
          refreshing={refreshMutation.isPending}
        />
      ) : null}

      {/* Payout account */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BanknoteIcon className="h-5 w-5" aria-hidden /> Your payout account
            </CardTitle>
            <CardDescription>
              Bank details and identity checks are handled by our payment provider on their own
              secure pages. Quibblies never sees or stores your bank or card numbers.
            </CardDescription>
          </div>
          {connected ? (
            <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
              Active
            </Badge>
          ) : connect ? (
            <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300">
              Setup incomplete
            </Badge>
          ) : (
            <Badge variant="secondary">Not connected</Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {connect?.disabledReason ? (
            <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              Payouts are paused: {connect.disabledReason.replaceAll("_", " ")}
            </p>
          ) : null}

          {connect && connect.requirementsDue.length > 0 ? (
            <div className="rounded-md bg-muted p-3 text-sm">
              <p className="font-medium">Still needed before you can be paid:</p>
              <ul className="mt-1 list-inside list-disc text-muted-foreground">
                {connect.requirementsDue.slice(0, 6).map((item) => (
                  <li key={item}>{item.replaceAll("_", " ").replace("individual.", "")}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {connected && data.connectBalances.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {data.connectBalances.map((balance) => (
                <div key={balance.currency} className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">
                    Available to pay out ({balance.currency})
                  </p>
                  <p className="font-display text-2xl">
                    {money(balance.availableMinor, balance.currency)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {money(balance.pendingMinor, balance.currency)} still clearing
                  </p>
                </div>
              ))}
            </div>
          ) : null}

          {data.connectError ? (
            <p className="text-sm text-muted-foreground">
              Live balances are briefly unavailable. Your earnings below are still accurate.
            </p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {connected ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => dashboardMutation.mutate()}
                  disabled={dashboardMutation.isPending}
                >
                  Open payout dashboard <ArrowUpRight className="ml-1 h-4 w-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => refreshMutation.mutate()}
                  disabled={refreshMutation.isPending}
                >
                  <RefreshCw className="mr-1 h-4 w-4" aria-hidden /> Refresh status
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => onboardMutation.mutate()}
                  disabled={onboardMutation.isPending}
                >
                  {connect ? "Finish payout setup" : "Set up automatic payouts"}
                </Button>
                {connect ? (
                  <Button
                    variant="ghost"
                    onClick={() => refreshMutation.mutate()}
                    disabled={refreshMutation.isPending}
                  >
                    <RefreshCw className="mr-1 h-4 w-4" aria-hidden /> Refresh status
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Balances per currency */}
      <section className="space-y-4">
        <h2 className="font-display text-2xl">Your earnings</h2>
        {data.totals.length === 0 ? (
          <EmptyState
            title="No tips yet"
            description="When a reader supports your writing, the amount will appear here with its settlement status."
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {data.totals.map((totals) => (
              <Card key={totals.currency}>
                <CardHeader>
                  <CardTitle className="text-lg">{totals.currency}</CardTitle>
                  <CardDescription>
                    {money(totals.lifetimeGrossMinor, totals.currency)} tipped in total ·{" "}
                    {money(totals.lifetimeFeeMinor, totals.currency)} platform fee
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Paid automatically</dt>
                      <dd className="font-display text-xl">
                        {money(totals.autoSettledMinor, totals.currency)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Owed to you</dt>
                      <dd className="font-display text-xl">
                        {money(totals.owedMinor, totals.currency)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Paid by our team</dt>
                      <dd>{money(totals.paidMinor, totals.currency)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Minimum payout</dt>
                      <dd>{money(data.minimumMinor, totals.currency)}</dd>
                    </div>
                  </dl>

                  {totals.owedMinor > 0 ? (
                    <div className="space-y-2 border-t pt-4">
                      <Textarea
                        value={notes[totals.currency] ?? ""}
                        onChange={(event) =>
                          setNotes((prev) => ({
                            ...prev,
                            [totals.currency]: event.target.value,
                          }))
                        }
                        placeholder="Anything our team should know about this payout (optional)"
                        rows={2}
                      />
                      <Button
                        className="w-full"
                        disabled={!totals.eligible || requestMutation.isPending}
                        onClick={() => requestMutation.mutate(totals.currency)}
                      >
                        Request payout
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        {totals.eligible
                          ? `Once approved, payments usually arrive within ${data.arrivalWindow}.`
                          : `You can request a payout once you've earned ${money(data.minimumMinor, totals.currency)}, or when your current request has been handled.`}
                      </p>
                    </div>
                  ) : (
                    <p className="flex items-center gap-2 border-t pt-4 text-xs text-muted-foreground">
                      <ShieldCheck className="h-4 w-4" aria-hidden />
                      {connected
                        ? "New tips settle straight to your own payout account."
                        : "Connect a payout account above so future tips settle automatically."}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Requests */}
      {data.requests.length > 0 ? (
        <section className="space-y-3">
          <h2 className="font-display text-2xl">Payout requests</h2>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Requested</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Team note</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.requests.map((request) => (
                  <Fragment key={request.id}>
                  <TableRow>
                    <TableCell>{shortDate(request.createdAt)}</TableCell>
                    <TableCell>{money(request.amountMinor, request.currency)}</TableCell>
                    <TableCell>
                      <Badge className={REQUEST_TONE[request.status] ?? ""}>
                        {request.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs text-sm text-muted-foreground">
                      {request.staffNote ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {request.status === "requested" ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" disabled={cancelMutation.isPending}>
                              Cancel request
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel this payout request?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Your balance stays exactly where it is and you can request a
                                payout again at any time. Once our team starts processing a
                                request it can no longer be cancelled here.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep request</AlertDialogCancel>
                              <AlertDialogAction onClick={() => cancelMutation.mutate(request.id)}>
                                Cancel request
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : null}
                    </TableCell>

                  </TableRow>
                  <TableRow className="bg-surface/30">
                    <TableCell colSpan={5}>
                      <RequestTimeline requestId={request.id} />
                    </TableCell>
                  </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      ) : null}

      {/* Settlement history */}
      <section className="space-y-3">
        <h2 className="font-display text-2xl">Payment history</h2>
        {data.connectPayouts.length === 0 && data.payouts.length === 0 ? (
          <EmptyState
            title="No payments sent yet"
            description="Payouts to your bank will be listed here with their reference and arrival date."
          />
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.connectPayouts.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell>{shortDate(payout.arrivalDate ?? payout.created)}</TableCell>
                    <TableCell>{money(payout.amountMinor, payout.currency)}</TableCell>
                    <TableCell>Automatic</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{payout.status}</Badge>
                      {payout.failureMessage ? (
                        <span className="ml-2 text-xs text-destructive">
                          {payout.failureMessage}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{payout.id}</TableCell>
                  </TableRow>
                ))}
                {data.payouts.map((payout) => (
                  <TableRow key={payout.id}>
                    <TableCell>{shortDate(payout.settledAt ?? payout.paidAt)}</TableCell>
                    <TableCell>{money(payout.amountMinor, payout.currency)}</TableCell>
                    <TableCell className="capitalize">
                      {payout.method.replaceAll("_", " ")}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{payout.state}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {payout.reference ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* Tip history */}
      <section className="space-y-3">
        <h2 className="font-display text-2xl">Tips received</h2>
        {data.tips.length === 0 ? (
          <EmptyState
            title="No tips yet"
            description="Support from readers will show up here as soon as it's received."
          />
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Publication</TableHead>
                  <TableHead>Tip</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead>You receive</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.tips.slice(0, 50).map((tip) => (
                  <TableRow key={tip.id}>
                    <TableCell>{shortDate(tip.completedAt ?? tip.createdAt)}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {tip.publicationId && tip.publicationTitle ? (
                        <Link
                          to="/story/$storyId"
                          params={{ storyId: tip.publicationId }}
                          className="underline-offset-4 hover:underline"
                        >
                          {tip.publicationTitle}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">Your profile</span>
                      )}
                    </TableCell>
                    <TableCell>{money(tip.grossMinor, tip.currency)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {money(tip.feeMinor, tip.currency)}
                    </TableCell>
                    <TableCell>{money(tip.netMinor, tip.currency)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {tip.status === "completed"
                          ? tip.settlementMode === "connect"
                            ? "Paid to you"
                            : tip.paidOut
                              ? "Paid out"
                              : "Awaiting payout"
                          : tip.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <p className="text-xs text-muted-foreground">
        Once a tip has been paid out to you it is final and non-refundable. See our{" "}
        <Link to="/tipping" className="underline underline-offset-4">
          tipping &amp; platform fee policy
        </Link>{" "}
        for the full terms.
      </p>
    </div>
  );
}
